import React, { createRef, forwardRef, useState } from 'react'
import { AppState } from '@halo/stores/app.state'
import { Input, Modal, notification, Switch } from 'antd'
import {
    cloneJob,
    destroySchedule,
    forceFailJob,
    patchSchedule,
    silenceScheduleAlarms,
    unsilenceScheduleAlarms,
} from '@halo/api/brine.api'
import { ModalFuncProps } from 'antd/lib/modal'

const DEFAULT_ACTION_MODAL_PROPS: Partial<ModalFuncProps> = {
    okText: 'Continue',
    okButtonProps: {
        className: 'job-schedule-confirmation-modal-ok-btn',
        size: 'small',
    },
    cancelButtonProps: {
        className: 'job-schedule-confirmation-modal-cancel-btn',
        size: 'small',
    },
}

const defaultFailureHandler = (res: any) => {
    notification.error({
        message: '',
        description: 'We were unable to update the schedule at this time. See console for more details.',
    })

    console.warn(res)
    return false
}

export const handleScheduleDisable = (schedule: any, onComplete: (ok: boolean, job: any) => void) => {
    Modal.confirm({
        ...DEFAULT_ACTION_MODAL_PROPS,
        title: 'Disable Schedule',
        content: (
            <>
                <p>Are you sure you want to disable the following schedule?</p>
                <p>
                    <b>Name:</b> {schedule.name}
                    <br />
                    <b>ID:</b> {schedule.id}
                    <br />
                </p>
            </>
        ),
        onOk: async (...args: any[]) => {
            const res = await patchSchedule(schedule.id, { enabled: false }, {})

            if (res.ok) {
                notification.success({
                    message: '',
                    description: 'Schedule successfully disabled.',
                })

                return onComplete(res.ok, res.data)
            } else {
                return defaultFailureHandler(res)
            }
        },
        onCancel: () => onComplete(false, null),
    })
}

export const handleScheduleEnable = async (schedule: any, onComplete: (ok: boolean, job: any) => void) => {
    const res = await patchSchedule(schedule.id, { enabled: true }, {})

    if (res.ok) {
        notification.success({
            message: '',
            description: 'Schedule successfully enabled.',
        })

        return onComplete(res.ok, res.data)
    } else {
        return defaultFailureHandler(res)
    }
}

export const handleScheduleAlarmSilence = (schedule: any, onComplete: (ok: boolean, job: any) => void) => {
    Modal.confirm({
        ...DEFAULT_ACTION_MODAL_PROPS,
        title: 'Silence Schedule Alarms',
        content: (
            <>
                <p>Are you sure you want to silence alarms for the following schedule?</p>
                <p>
                    <b>Name:</b> {schedule.name}
                    <br />
                    <b>ID:</b> {schedule.id}
                    <br />
                </p>
            </>
        ),
        onOk: async (...args: any[]) => {
            const res = await silenceScheduleAlarms(schedule.id, {})

            if (res.ok) {
                notification.success({
                    message: '',
                    description: 'Schedule alarms successfully updated.',
                })

                return onComplete(res.ok, res.data)
            } else {
                return defaultFailureHandler(res)
            }
        },
        onCancel: () => onComplete(false, null),
    })
}

export const handleScheduleAlarmUnsilence = async (schedule: any, onComplete: (ok: boolean, job: any) => void) => {
    const res = await unsilenceScheduleAlarms(schedule.id, {})

    if (res.ok) {
        notification.success({
            message: '',
            description: 'Schedule alarms successfully updated.',
        })

        return onComplete(res.ok, res.data)
    } else {
        return defaultFailureHandler(res)
    }
}

export const handleScheduleDestroy = (schedule: any, onComplete: (ok: boolean, job: any) => void) => {
    Modal.confirm({
        ...DEFAULT_ACTION_MODAL_PROPS,
        title: 'Delete Schedule',
        content: (
            <>
                <p>Are you sure you want to (soft) delete the following schedule?</p>
                <p>
                    <b>Name:</b> {schedule.name}
                    <br />
                    <b>ID:</b> {schedule.id}
                    <br />
                </p>
            </>
        ),
        onOk: async (...args: any[]) => {
            const res = await destroySchedule(schedule.id, {})

            if (res.ok) {
                notification.success({
                    message: '',
                    description: 'Schedule successfully deleted.',
                })

                return onComplete(res.ok, res.data)
            } else {
                return defaultFailureHandler(res)
            }
        },
        onCancel: () => onComplete(false, null),
    })
}
